export default function PembrokePinesDestinationsSection() {
  const stats = [
    {
      value: "55%",
      label: "of Pembroke Pines requests are Caribbean cruise or Latin American family visit departures with under 3 weeks notice",
    },
    {
      value: "2–4 weeks",
      label: "average notice for business trips to Latin America from Pembroke Pines professionals",
    },
    {
      value: "Mar–Jun",
      label: "peak cruise and spring break season — passport renewals spike just before sailing dates",
    },
    {
      value: "36-hour",
      label: "shortest cruise departure window we've accommodated for a Pembroke Pines family",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "Colombia & Venezuela",
      context: "The most culturally significant destinations for Pembroke Pines travelers — Bogota, Medellin, Cali, and Cartagena for Colombian families; Caracas, Valencia, and Margarita Island for Venezuelan families. These trips represent family reunions, heritage visits, and business connections that cannot be postponed. Most travelers fly from FLL or MIA with direct or connecting service.",
      urgency: "Family emergencies and milestone celebrations operate on fixed dates — passport discoveries 2–3 weeks before departure are common",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "Cruises from Port Everglades and flights from FLL to Nassau, San Juan, Montego Bay, and Punta Cana are staples for Pembroke Pines families. Spring break and summer vacations revolve around Caribbean beach resorts and cruise itineraries. All cruise passengers and most air travelers need valid passports with six months validity.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, and Los Cabos are popular spring break and summer destinations for Pembroke Pines families seeking beach vacations within a short flight. Business travelers also fly to Mexico City and Guadalajara for corporate meetings. Mexico requires six months of passport validity for entry.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March for March/April departures",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Israel",
      context: "Bar and bat mitzvah trips, family heritage tours, and religious pilgrimage travel to Israel are significant for Pembroke Pines' well-established Jewish community. Group travel is common, with fixed dates tied to religious calendars and school schedules. Jerusalem, Tel Aviv, Haifa, and the Dead Sea are primary destinations.",
      urgency: "Bar/bat mitzvah dates and religious calendars are fixed — passport delays can jeopardize years of planning and significant financial deposits",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Cuba & Dominican Republic",
      context: "Cuban heritage travel for family reunions and cultural visits, plus Dominican Republic resort vacations for Caribbean getaways. Miami serves as the primary gateway for Cuba travel, while the Dominican Republic is accessible via FLL and MIA. Both destinations require valid passports and have specific entry requirements.",
      urgency: "Heritage travel often ties to family milestones and fixed dates — last-minute passport issues are common when booking 3–4 weeks out",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe & Canada",
      context: "London, Paris, Rome, and Barcelona for summer cultural tours and educational travel. Toronto, Montreal, and Vancouver for business conferences and ski trips. Pembroke Pines' upwardly mobile families and business professionals regularly travel to Europe and Canada, particularly during summer break and for professional conferences.",
      urgency: "European villa rental deposits and tour bookings are typically non-refundable with short cancellation windows — the Schengen Area requires 3 months validity beyond departure",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Pembroke Pines Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Pembroke Pines
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Pembroke Pines travelers head to Colombia, Venezuela, the Caribbean, Mexico, Israel, and Europe for a mix of family heritage visits, business travel, cruises, and religious pilgrimage. These are the destinations that create the most passport urgency for our Pembroke Pines clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Plantation", href: "/cities/plantation" },
              { name: "Hollywood", href: "/cities/hollywood" },
              { name: "Coral Springs", href: "/cities/coral-springs" },
              { name: "Weston", href: "/cities/weston" },
              { name: "Miramar", href: "#" },
              { name: "Fort Lauderdale", href: "#" },
            ].map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}